import { Card } from "antd";
import axios from 'axios';
import L from 'leaflet';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useState } from 'react';
import { FeatureGroup, MapContainer, Marker, Polygon, Polyline, Rectangle, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import './style.css';
import { useDispatch, useSelector } from "react-redux";
import { CompanyOfficeDetailsGetbyCompanyId, CompanyOfficeDetailsGetbyOfficeId } from "../../redux/rtk/features/settingSlice/settingSlice";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png",
});

const OfficePage = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [map, setMap] = useState(null);
  const [createdShapeCoordinates, setCreatedShapeCoordinates] = useState([]);
  const [shapeType, setShapeType] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [customCoordinates, setCustomCoordinates] = useState({ lat: '', lon: '' });
  const [officeNames, setOfficeNames] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const dispatch = useDispatch();
  const companyName = useSelector((state) => state?.setting?.companyOfficeDetail);
  const officeDetail = useSelector((state) => state?.setting?.OfficeDetail);
  const CompanyId = 1;

  useEffect(() => {
    // Dispatch action to fetch company office details
    dispatch(CompanyOfficeDetailsGetbyCompanyId(CompanyId));
  }, [dispatch]);

  useEffect(() => {
    // Update office names when companyName (Redux state) changes
    if (companyName && Array.isArray(companyName)) {
      const officeNamesArray = companyName.map(office => ({
        id: office.CompanyOfficeId,
        name: office.CompanyOfficeName,
      }));
      setOfficeNames(officeNamesArray);
    } else {
      setOfficeNames([]);
    }
  }, [companyName]);

  useEffect(() => {
    // Fetch office details when selectedOffice changes
    if (selectedOffice) {
      dispatch(CompanyOfficeDetailsGetbyOfficeId(selectedOffice));
    }
  }, [selectedOffice, dispatch]);

  useEffect(() => {
    // Update map when officeDetail (Redux state) changes
    if (officeDetail && Array.isArray(officeDetail) && officeDetail.length > 0) {
      const details = officeDetail[0];
      // Parse CompanyOfficeArea to display on map
      if (details.CompanyOfficeArea) {
        const coords = parsePolygon(details.CompanyOfficeArea);
        setCreatedShapeCoordinates(coords);
        setShapeType('polygon');
        if (map && coords.length > 0) {
          const bounds = L.latLngBounds(coords);
          map.fitBounds(bounds);
        }
      } else {
        setCreatedShapeCoordinates([]);
        setShapeType(null);
      }
    } else {
      setCreatedShapeCoordinates([]);
      setShapeType(null);
    }
  }, [officeDetail, map]);

  useEffect(() => {
    // Fetch user location when the component mounts
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation([position.coords.latitude, position.coords.longitude]);
      },
      (error) => {
        // console.error('Error getting user location:', error.message);
      }
    );
  }, []);

  // Parse POLYGON string to Leaflet-compatible coordinates
  const parsePolygon = (polygonString) => {
    if (!polygonString || !polygonString.startsWith('POLYGON ((')) return [];
    const coordsString = polygonString.replace('POLYGON ((', '').replace('))', '');
    const coordPairs = coordsString.split(', ').map(pair => {
      const [lon, lat] = pair.split(' ').map(Number);
      return [lat, lon]; // Leaflet expects [lat, lon]
    });
    return coordPairs;
  };

  const LocateControl = () => {
    const mapInstance = useMapEvents({
      click() {
        mapInstance.locate();
      },
      locationfound(e) {
        setUserLocation([e.latlng.lat, e.latlng.lng]);
        mapInstance.flyTo(e.latlng, mapInstance.getZoom());
      },
    });

    // Save the map instance to the state
    setMap(mapInstance);

    return null;
  };

  const _created = (e) => {
    const { layerType, layer } = e;
    let coords = [];
    if (layerType === 'polygon') {
      coords = layer.getLatLngs()[0].map(latlng => [latlng.lat, latlng.lng]);
      // Ensure the polygon is closed
      if (coords.length > 0) {
        coords.push(coords[0]);
      }
    } else if (layerType === 'polyline') {
      coords = layer.getLatLngs().map(latlng => [latlng.lat, latlng.lng]);
    } else if (layerType === 'rectangle') {
      const bounds = layer.getBounds();
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      coords = [
        [sw.lat, sw.lng],
        [ne.lat, sw.lng],
        [ne.lat, ne.lng],
        [sw.lat, ne.lng],
        [sw.lat, sw.lng],
      ];
    } else {
      // console.log('Unsupported layer type:', layerType);
      return;
    }
    setCreatedShapeCoordinates(coords);
    setShapeType(layerType);
    // console.log(`${layerType} Coordinates:`, coords);
  };

  const handleEnteredCoordinates = () => {
    // Update the map with the custom coordinates entered by the user
    const newLocation = [parseFloat(customCoordinates.lat), parseFloat(customCoordinates.lon)];
    setUserLocation(newLocation);
    if (map) {
      map.flyTo(newLocation, map.getZoom());
    } else {
      // console.error('Map is null or undefined.');
    }
  };

  const handleClearCoordinates = () => {
    setCreatedShapeCoordinates([]);
    setShapeType(null);
  };

  const handleAddOffice = async () => {
    if (!selectedOffice) {
      // console.error('Please select an office');
      return;
    }
    if (!createdShapeCoordinates || createdShapeCoordinates.length < 2) {
      // console.error('Please draw a valid shape');
      return;
    }

    const officeData = {
      CompanyOfficeId: parseInt(selectedOffice), // Use selected CompanyOfficeId
      PointsDetails: createdShapeCoordinates.map((point, index) => ({
        Latitude: point[0],
        Longitude: point[1],
        PointOrder: index,
      })),
      DataStatus: 1,
    };

    try {
      await axios.post('company/CompanyOfficeAreaSetup', officeData);
      setSuccessMessage('Office area added successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
      // Refresh office details
      dispatch(CompanyOfficeDetailsGetbyOfficeId(selectedOffice));
    } catch (error) {
      // console.error('Error adding office area:', error.message);
    }
  };

  const handleUpdateOffice = async () => {
    if (!selectedOffice) {
      // console.error('Please select an office');
      return;
    }

    // Clear existing coordinates to allow drawing a new shape
    handleClearCoordinates();
    setSuccessMessage('Please draw a new area to update.');
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleConfirmUpdate = async () => {
    if (!selectedOffice) {
      // console.error('Please select an office');
      return;
    }
    if (!createdShapeCoordinates || createdShapeCoordinates.length < 2) {
      // console.error('Please draw a valid shape');
      return;
    }

    const officeData = {
      CompanyOfficeId: parseInt(selectedOffice),
      PointsDetails: createdShapeCoordinates.map((point, index) => ({
        Latitude: point[0],
        Longitude: point[1],
        PointOrder: index,
      })),
      DataStatus: 1,
      CreatedBy: "0",
      ModifiedBy: "0",
    };

    try {
      await axios.post('company/CompanyOfficeAreaSetup', officeData);
      setSuccessMessage('Office area updated successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
      // Refresh office details
      dispatch(CompanyOfficeDetailsGetbyOfficeId(selectedOffice));
    } catch (error) {
      // console.error('Error updating office area:', error.message);
    }
  };

  return (
    <div className="office-container bg-gray-100 min-h-screen p-6">
      <Card className="shadow-lg rounded-lg mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Set Office Coordinates</h2>
      </Card>

      <div className="content-container  mx-auto">
        <div className="page-content bg-white p-6 rounded-lg shadow-md">
          <div className="mb-4">
            <label htmlFor="officeName" className="block text-sm font-medium text-gray-700 mb-1">
              Office Name
            </label>
            <select
              id="officeName"
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Office</option>
              {officeNames.map((office) => (
                <option key={office.id} value={office.id}>
                  {office.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="customLat" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="text"
                id="customLat"
                placeholder="Enter latitude"
                value={customCoordinates.lat}
                onChange={(e) => setCustomCoordinates({ ...customCoordinates, lat: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="customLon" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="text"
                id="customLon"
                placeholder="Enter longitude"
                value={customCoordinates.lon}
                onChange={(e) => setCustomCoordinates({ ...customCoordinates, lon: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200"
                onClick={handleEnteredCoordinates}
              >
                Locate Coordinates
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mb-4">
            {/* <button
              className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition duration-200"
              onClick={handleAddOffice}
            >
              Add Area
            </button> */}
            <button
              className="bg-yellow-600 text-white py-2 px-4 rounded-md hover:bg-yellow-700 transition duration-200"
              onClick={handleUpdateOffice}
            >
              Update Area
            </button>
            {createdShapeCoordinates.length > 0 && (
              <button
                className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition duration-200"
                onClick={handleConfirmUpdate}
              >
                Confirm Update
              </button>
            )}
          </div>
          {successMessage && (
            <div className="success-message text-green-600 bg-green-100 p-3 rounded-md mb-4">
              {successMessage}
            </div>
          )}
        </div>
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Map Area</label>
          <MapContainer
            style={{ width: '100%', height: '500px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            center={userLocation || [28.7041, 77.1025]}
            zoom={userLocation ? 15 : 8}
          >
            <FeatureGroup>
              <EditControl
                position='topright'
                onCreated={_created}
                draw={{ rectangle: true, circle: false, circlemarker: false, marker: false, polyline: true, polygon: true }}
              ></EditControl>
            </FeatureGroup>
            <TileLayer
              attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {userLocation && (
              <Marker position={userLocation}>
                <Popup>
                  <div>
                    <strong>Your Location</strong>
                    <p>Latitude: {userLocation[0]}</p>
                    <p>Longitude: {userLocation[1]}</p>
                  </div>
                </Popup>
              </Marker>
            )}
            {createdShapeCoordinates.length > 0 && shapeType === 'polygon' && (
              <Polygon positions={createdShapeCoordinates} />
            )}
            {createdShapeCoordinates.length > 0 && shapeType === 'polyline' && (
              <Polyline positions={createdShapeCoordinates} />
            )}
            {createdShapeCoordinates.length > 0 && shapeType === 'rectangle' && (
              <Rectangle bounds={createdShapeCoordinates} />
            )}
            <LocateControl />
          </MapContainer>
        </div>
        {attendanceStatus && (
          <div className="mt-4 text-gray-600">{attendanceStatus}</div>
        )}
      </div>
    </div>
  );
};

export default OfficePage;

